import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import MaterialForm from '../components/MaterialForm';
import ConfirmationModal from '../components/ConfirmationModal';
import { useToast } from '../hooks/useToast';

const API_PREFIX = '/internal/materials/global';

export default function AdminMaterials() {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    showInSite: 'all',
  });

  const { showToast } = useToast();

  const fetchMaterials = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/materials?ownership=system');
      setMaterials(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch system materials');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  const filteredMaterials = (() => {
    let filtered = [...materials];

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(m =>
        m.name?.toLowerCase().includes(search) ||
        m.key?.toLowerCase().includes(search)
      );
    }

    if (filters.showInSite !== 'all') {
      filtered = filtered.filter(m => m.showInSite === (filters.showInSite === 'true'));
    }

    filtered.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

    return filtered;
  })();

  const handleAdd = () => {
    setEditingMaterial(null);
    setShowForm(true);
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setShowForm(true);
  };

  const handleDelete = (material) => {
    setDeleteTarget(material);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`${API_PREFIX}/${deleteTarget._id}`);
      showToast(`Material "${deleteTarget.name}" deactivated`, 'success');
      setDeleteTarget(null);
      fetchMaterials();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete material', 'error');
    }
  };

  const handleSave = async (data) => {
    const isEditing = !!editingMaterial;
    const isFormData = data instanceof FormData;
    const config = isFormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};
    if (isEditing) {
      await api.patch(`${API_PREFIX}/${editingMaterial._id}`, data, config);
    } else {
      await api.post(API_PREFIX, data, config);
    }
    setShowForm(false);
    setEditingMaterial(null);
    fetchMaterials();
    showToast(
      isEditing ? 'System material updated successfully!' : 'System material created successfully!',
      'success'
    );
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'key', label: 'Key', sortable: true },
    {
      key: 'pricePerPrintedKg',
      label: 'Price/Kg',
      render: (val) => val ? `€${Number(val).toFixed(2)}` : '€1.00'
    },
    {
      key: 'showInSite',
      label: 'Show in Site',
      render: (val) => (val ? '✓' : '✗')
    },
    { key: 'displayOrder', label: 'Order', sortable: true },
    {
      key: 'createdAt',
      label: 'Created',
      render: (val) => new Date(val).toLocaleDateString()
    }
  ];

  const renderFilters = () => (
    <div className="filters" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by name or key..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          style={{ flex: '1 1 300px' }}
        />
        <select
          value={filters.showInSite}
          onChange={(e) => setFilters({ ...filters, showInSite: e.target.value })}
          style={{ flex: '1 1 150px' }}
        >
          <option value="all">All Visibility</option>
          <option value="true">Show in Site</option>
          <option value="false">Hidden from Site</option>
        </select>
      </div>
    </div>
  );

  if (loading && materials.length === 0) return <div>Loading...</div>;
  if (error && materials.length === 0) return <div className="error">{error}</div>;

  return (
    <div>
      {showForm ? (
        <MaterialForm
          material={editingMaterial}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingMaterial(null);
          }}
        />
      ) : (
        <DataTable
          title="System Material Presets"
          data={filteredMaterials}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onAdd={handleAdd}
          renderFilters={renderFilters}
        />
      )}

      {deleteTarget && (
        <ConfirmationModal
          title="Delete System Material"
          message={`Are you sure you want to deactivate "${deleteTarget.name}"? Operators who already duplicated it will not be affected.`}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
          confirmText="Deactivate"
        />
      )}
    </div>
  );
}
